/**
 * sm-analytics — behavioral event listeners for the core bundle.
 *
 * One module owns every storefront-wide behavioral event from
 * library/analytics/TRACKING_PLAN.md that isn't tied to the plans/quiz
 * bundles:
 *
 *   filter_applied            facet checkboxes/price inputs + sort selects
 *   search_suggestion_click   predictive-search result clicks
 *   select_variant            PDP variant changes (pub/sub `variant-change`)
 *   gallery_interaction       PDP GalleryViewer swipe/thumbnail/zoom
 *                             (carousel swipes live in the swiper
 *                             `analytics` feature plugin, not here)
 *   cta_click                 [data-sm-track="cta_click"] + automatic
 *                             quiz-entry link detection
 *   promo_banner_click        announcement-bar links
 *   contact_form_submitted    [data-sm-track-once] posted-success markers
 *
 * Design rules (see TRACKING_PLAN.md §3):
 *   - Event delegation on stable selectors/data attributes — nothing binds
 *     to markup that a Section Rendering swap would orphan (facets re-render
 *     their whole grid; delegation on document survives it).
 *   - High-frequency sources are debounced/deduped (variant toggling,
 *     gallery slides).
 *   - All pushes go through smTrack (consent gate + envelope + truncation).
 *   - No PII: search params carry the typed query only, form events carry
 *     no field values.
 */

// window.smTrack is registered by assets/sm-track.js (its own script tag,
// loaded before this bundle — see that file's LOADING MODEL note for why
// this is a global instead of an import). No-op-safe if it's ever missing.
// The accessor name must be UNIQUE ACROSS BUNDLES: these build to classic
// scripts whose top-level const/let share the page's global lexical scope,
// so two bundles both declaring `const smTrack` throw "already declared"
// and kill the second bundle wholesale.
const smTrackApp = (eventName, params) => {
  if (typeof window !== 'undefined' && typeof window.smTrack === 'function') {
    window.smTrack(eventName, params);
  }
};

const VARIANT_DEBOUNCE_MS = 500;

// ---------- discovery: filters / sort ----------

function collectionHandle() {
  const match = window.location.pathname.match(/\/collections\/([^/]+)/);
  return match ? match[1] : '';
}

function onFilterChange(event) {
  const input = event.target;
  if (!input || !input.name) return;

  // Sort selects: the classic grid renders <select name="sort_by"> (#SortBy);
  // v3 facets render their own select with the same name.
  if (input.name === 'sort_by') {
    smTrackApp('filter_applied', {
      filter_type: 'sort_by',
      filter_value: String(input.value),
      filter_action: 'add',
      item_list_id: collectionHandle(),
    });
    return;
  }

  // Facet inputs live inside <facet-filters-form> and are named after the
  // storefront filter param (filter.v.option.color, filter.v.price.gte, …).
  if (input.name.indexOf('filter.') === 0 && input.closest('facet-filters-form')) {
    const isCheckbox = input.type === 'checkbox';
    smTrackApp('filter_applied', {
      filter_type: input.name,
      filter_value: String(input.value),
      filter_action: isCheckbox && !input.checked ? 'remove' : 'add',
      item_list_id: collectionHandle(),
    });
  }
}

// ---------- PDP: variant selection ----------

let variantDebounceTimer = null;
let lastTrackedVariantId = null;

function wireVariantTracking() {
  // pubsub.js/constants.js are classic pre-bundle scripts; their globals are
  // guaranteed evaluated before this module (core.js loads as a deferred
  // module). Guard anyway — some templates omit them.
  if (typeof window.subscribe !== 'function') return;
  const eventName =
    (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) || 'variant-change';

  window.subscribe(eventName, (event) => {
    const variant = event && event.data && event.data.variant;
    if (!variant || !variant.id) return;

    // Debounce rapid toggling; dedupe re-selecting the current variant.
    clearTimeout(variantDebounceTimer);
    variantDebounceTimer = setTimeout(() => {
      if (String(variant.id) === String(lastTrackedVariantId)) return;
      lastTrackedVariantId = variant.id;
      const productIdInput = document.querySelector('input[name="product-id"]');
      smTrackApp('select_variant', {
        item_id: productIdInput ? String(productIdInput.value) : '',
        item_variant: String(variant.id),
        // variant.title carries every selected option ("32 / 30 / Indigo"),
        // which covers multi-option products (waist/inseam/color) in one
        // param — per-option name/value pairs can't (TRACKING_PLAN §3.3).
        option_value: variant.title || '',
        variant_available: String(variant.available !== false),
      });
    }, VARIANT_DEBOUNCE_MS);
  });
}

// ---------- PDP: media gallery ----------

// One event per media index per page view (per TRACKING_PLAN §3.4).
const seenMediaIndexes = new Set();

function mediaTypeOf(element) {
  if (!element) return 'image';
  if (element.querySelector('model-viewer, product-model')) return 'model';
  if (element.querySelector('video, iframe, deferred-media')) return 'video';
  return 'image';
}

function trackGallery(interactionType, mediaIndex, mediaType) {
  const dedupeKey = interactionType + ':' + mediaIndex;
  if (seenMediaIndexes.has(dedupeKey)) return;
  seenMediaIndexes.add(dedupeKey);
  const productIdInput = document.querySelector('input[name="product-id"]');
  smTrackApp('gallery_interaction', {
    interaction_type: interactionType,
    media_index: mediaIndex,
    media_type: mediaType,
    item_id: productIdInput ? String(productIdInput.value) : '',
  });
}

function wireGalleryTracking() {
  // Dawn's SliderComponent dispatches a non-bubbling `slideChanged`
  // CustomEvent on itself, so this needs direct bindings, re-checked on
  // section re-render via shopify:section:load.
  document.querySelectorAll('slider-component[id^="GalleryViewer"]').forEach((gallery) => {
    if (gallery.dataset.smAnalyticsWired) return;
    gallery.dataset.smAnalyticsWired = 'true';
    gallery.addEventListener('slideChanged', (event) => {
      const detail = event.detail || {};
      trackGallery(
        'swipe',
        typeof detail.currentPage === 'number' ? detail.currentPage : -1,
        mediaTypeOf(detail.currentElement)
      );
    });
  });
}

// ---------- delegated clicks ----------

/** cta_id for a quiz-entry link, derived from where the link lives. */
function quizCtaId(link) {
  if (link.closest('header-drawer, menu-drawer')) return 'quiz_drawer';
  if (link.closest('cart-drawer')) return 'quiz_cart_drawer';
  if (link.closest('header, .header')) return 'quiz_header';
  const section = link.closest('[id^="shopify-section-"]');
  if (section) {
    // Keep the human-readable tail of the section id ("…__hero_banner_x7Kp").
    const tail = section.id.split('__').pop();
    return 'quiz_section_' + tail;
  }
  return 'quiz_other';
}

function onDelegatedClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  // 1. Explicit opt-in markers: <el data-sm-track="cta_click" data-sm-cta="…">
  const marked = target.closest('[data-sm-track]');
  if (marked) {
    const eventName = marked.dataset.smTrack;
    if (eventName === 'cta_click') {
      smTrackApp('cta_click', {
        cta_id: marked.dataset.smCta || 'unlabeled',
        link_url: marked.getAttribute('href') || '',
      });
    } else if (eventName === 'promo_banner_click') {
      smTrackApp('promo_banner_click', {
        banner_id: marked.dataset.smBannerId || '',
        banner_text: (marked.textContent || '').trim(),
        link_url: marked.getAttribute('href') || '',
      });
    }
    // Other names are ignored here on purpose: interaction events that need
    // richer context (plans, quiz) fire from their own modules.
    return;
  }

  // 2. Quiz entry CTAs — every /pages/quiz link, no attributes needed.
  const quizLink = target.closest('a[href^="/pages/quiz"]');
  if (quizLink) {
    smTrackApp('cta_click', {
      cta_id: quizCtaId(quizLink),
      link_url: quizLink.getAttribute('href') || '',
    });
    return;
  }

  // 3. Announcement-bar promo links (theme-owned markup; the Hextom app bar
  //    is app-rendered and deliberately not tracked — TRACKING_PLAN §3.13).
  const promoLink = target.closest('a.announcement-bar__link');
  if (promoLink) {
    const section = promoLink.closest('[id^="shopify-section-"]');
    smTrackApp('promo_banner_click', {
      banner_id: section ? section.id.replace('shopify-section-', '') : 'announcement-bar',
      banner_text: (promoLink.textContent || '').trim(),
      link_url: promoLink.getAttribute('href') || '',
    });
    return;
  }

  // 4. Predictive-search suggestion clicks.
  const suggestion = target.closest('#predictive-search-results a, #predictive-search-results button');
  if (suggestion) {
    const searchInput = document.querySelector('predictive-search input[type="search"]');
    const href = suggestion.getAttribute('href') || '';
    const productMatch = href.match(/\/products\/([^/?#]+)/);
    const group = suggestion.closest('.predictive-search__result-group');
    const items = group
      ? Array.from(group.querySelectorAll('a, button'))
      : [suggestion];
    smTrackApp('search_suggestion_click', {
      search_term: searchInput ? searchInput.value : '',
      suggestion_type: productMatch ? 'product' : href.indexOf('/search') === 0 ? 'query' : 'page',
      suggestion_value: productMatch ? productMatch[1] : (suggestion.textContent || '').trim(),
      index: items.indexOf(suggestion),
    });
    return;
  }

  // 5. PDP gallery: thumbnails and the zoom lightbox button.
  const zoomButton = target.closest('.product__media-zoom-lightbox');
  if (zoomButton) {
    trackGallery('zoom', -1, 'image');
    return;
  }
  const thumbnail = target.closest('button.thumbnail[data-target], .thumbnail-list button');
  if (thumbnail) {
    const list = thumbnail.closest('.thumbnail-list');
    const index = list
      ? Array.from(list.querySelectorAll('button')).indexOf(thumbnail)
      : -1;
    trackGallery('thumbnail', index, 'image');
  }
}

// ---------- posted-success markers ----------

// Liquid renders <el data-sm-track-once="contact_form_submitted"
// data-sm-form-id="contact"> only on a successful form POST; presence at
// load time IS the success signal (fire-on-success convention).
function fireRenderMarkers() {
  document.querySelectorAll('[data-sm-track-once]').forEach((marker) => {
    if (marker.dataset.smFired) return;
    marker.dataset.smFired = 'true';
    smTrackApp(marker.dataset.smTrackOnce, {
      form_id: marker.dataset.smFormId || '',
    });
  });
}

// ---------- bootstrap ----------

function init() {
  document.addEventListener('click', onDelegatedClick);
  document.addEventListener('change', onFilterChange);
  wireVariantTracking();
  wireGalleryTracking();
  fireRenderMarkers();
  // Section Rendering swaps (theme editor, facet re-renders) can introduce
  // fresh gallery components; re-wire is idempotent via the data marker.
  document.addEventListener('shopify:section:load', wireGalleryTracking);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}
